/* =========================================================================
   "What shall we study today?" — the round category picker (PLAN-gameplay-v2).

   Players with the picker unlocked (server-decided: ≥ 10 lifetime completed
   rounds) choose the round's category at every round intro; "Mixed" is the
   effortless one-tap default and exactly today's behaviour. The choice is PURE
   PREFERENCE — no stakes/XP/difficulty changes — so the module never implies
   any. It runs BEFORE the server draw (gameController awaits it ahead of
   loadRoundQuestions) because the chosen groupKey shapes that draw; guests,
   locked and offline players never see it and keep today's flow byte-for-byte.

   House pattern: pure logic (DTO normalization, icon assignment) exported for
   tests; DOM rendering isolated in showRoundPicker(); no api import, so this
   module can never form an import cycle with api.ts (which imports the
   normalizer, mirroring how api.ts consumes serverGame's normalizers).
   ========================================================================= */
import { prefersReducedMotion } from './ui/momentOverlay';

/* ----------------------------- server DTO ----------------------------- */

/** One category tile of GET /api/game/round-options. */
export interface RoundOptionGroup {
  key: string;
  title: string;
  count: number;
}

/** GET /api/game/round-options, normalized. `unlocked:false` = show nothing. */
export interface RoundOptions {
  unlocked: boolean;
  roundsCompleted: number;
  groups: RoundOptionGroup[];
}

/** The safe default: everything off. A fresh object each call (callers may own it). */
export function lockedOptions(): RoundOptions {
  return { unlocked: false, roundsCompleted: 0, groups: [] };
}

/** More tiles than this and the 2-column grid stops being a quick pick —
    defensively cap a runaway payload instead of rendering it. */
const MAX_GROUPS = 12;

/** Non-negative integer from an unknown, or null when it isn't one. */
function nonNegInt(v: unknown): number | null {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  const n = Math.floor(v);
  return n >= 0 ? n : null;
}

/**
 * Defensive read of the round-options payload. ANY off-shape — wrong type,
 * `unlocked` not exactly true, groups missing/empty/all-malformed — collapses
 * to locked, so a server hiccup can only ever cost the picker, never the round.
 * Malformed group rows are dropped individually; duplicate keys keep the first.
 * PURE.
 */
export function normalizeRoundOptions(raw: unknown): RoundOptions {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return lockedOptions();
  const r = raw as Record<string, unknown>;
  if (r.unlocked !== true) return lockedOptions();
  if (!Array.isArray(r.groups)) return lockedOptions();

  const groups: RoundOptionGroup[] = [];
  const seen = new Set<string>();
  for (const row of r.groups) {
    if (groups.length >= MAX_GROUPS) break;
    if (!row || typeof row !== 'object') continue;
    const g = row as Record<string, unknown>;
    const key = typeof g.key === 'string' ? g.key.trim() : '';
    const title = typeof g.title === 'string' ? g.title.trim() : '';
    const count = nonNegInt(g.count);
    if (!key || !title || count === null) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    groups.push({ key, title, count });
  }
  // Unlocked with zero usable tiles is a picker with nothing to pick — Mixed is
  // already the default behaviour, so treat it as locked and show nothing.
  if (groups.length === 0) return lockedOptions();
  return { unlocked: true, roundsCompleted: nonNegInt(r.roundsCompleted) ?? 0, groups };
}

/** Should the picker show at all? (gameController's single gate.) PURE. */
export function pickerEligible(options: RoundOptions | null | undefined): boolean {
  return !!options && options.unlocked === true && options.groups.length > 0;
}

import { icon, type IconName } from './ui/icons';

/* --------------------------- icons + tile tints --------------------------- */

/** Curated brand icons (ui/icons.ts) for the group keys we expect the server
    to seed. Lookup is case-insensitive; unknown keys hash into ICON_POOL so
    every key keeps a FIXED icon across renders and games. */
const ICON_BY_KEY: Record<string, IconName> = {
  bible: 'book',
  'bible-stories': 'book',
  gospels: 'book',
  saints: 'medal',
  liturgy: 'candle',
  popes: 'key',
  papacy: 'key',
  catechism: 'book',
  prayers: 'rosary',
  prayer: 'rosary',
  mass: 'church',
  'the-mass': 'church',
  history: 'church',
  'church-history': 'church',
  chosen: 'book',
  'the-chosen': 'book',
  mary: 'star',
  sacraments: 'candle',
  orders: 'dove',
  'religious-orders': 'dove',
  miracles: 'sparkle',
};

const ICON_POOL: IconName[] = ['book', 'medal', 'candle', 'key', 'cross', 'rosary', 'church', 'compass', 'star', 'dove', 'sparkle', 'flame'];

/** Tiny deterministic string hash (djb2 xor variant) — stable icon per key. */
function hashKey(key: string): number {
  let h = 5381;
  for (let i = 0; i < key.length; i++) h = ((h * 33) ^ key.charCodeAt(i)) >>> 0;
  return h;
}

/** The fixed brand icon for a group key: curated when known, hashed otherwise. PURE. */
export function iconForGroup(key: string): IconName {
  const k = key.trim().toLowerCase();
  return ICON_BY_KEY[k] ?? ICON_POOL[hashKey(k) % ICON_POOL.length];
}

/** Soft tile tints cycle by grid position (like the mockup) — 6 CSS classes. */
export const TINT_COUNT = 6;
export function tintClassForIndex(i: number): string {
  return 'rpk-tint' + (((i % TINT_COUNT) + TINT_COUNT) % TINT_COUNT);
}

/* ------------------------------- rendering ------------------------------- */

const HEADLINE = 'What shall we study today?'; // Brother Theo's line — exact copy
const THEO_EMOJI = '🧑‍🦲';
/** Theo's conversation portrait slot — same graceful probe pattern as gtky.ts:
    the emoji fills the badge until Eric's art lands (zero code edits then). */
const HEADSHOT_PATH = 'media/characters/theo-headshot.png';
let headshotAvail = false;
let headshotProbed = false;

function probeHeadshot(): void {
  if (headshotProbed) return;
  headshotProbed = true;
  try {
    const img = new Image();
    img.onload = () => {
      headshotAvail = true;
      fillTheoBadge(); // art landed while the picker is up — swap it in
    };
    img.onerror = () => {
      /* asset not present yet — emoji fallback stays */
    };
    img.src = HEADSHOT_PATH;
  } catch {
    /* no Image() (tests) — emoji fallback */
  }
}

function fillTheoBadge(): void {
  const badge = document.getElementById('rpTheo');
  if (!badge) return;
  badge.innerHTML = headshotAvail
    ? `<img src="${HEADSHOT_PATH}" alt="" />`
    : `<span class="rpk-theo-emoji">${THEO_EMOJI}</span>`;
}

/** Minimal HTML escape (mirror of engine.esc, local so this module stays leaf). */
function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export interface RoundPickerOpts {
  /** 1-based round number for the kicker ("ROUND 4 · BIBLE STORIES"). */
  roundNumber: number;
  totalRounds: number;
  /** The round's display name (kicker uppercases it via CSS). */
  roundName: string;
  /** The career chip text ("Level 12") or null to hide it. */
  careerChip: string | null;
  groups: RoundOptionGroup[];
}

const OVERLAY_ID = 'roundPickerOv';

function host(): HTMLElement {
  return (document.querySelector('.screen') as HTMLElement | null) || document.body;
}

/**
 * Show the picker and resolve the chosen group key — or null for Mixed (tap on
 * the primary Mixed action, Escape, or any resolution failure). Resolves
 * exactly once; the overlay removes itself on dismiss (respecting
 * prefers-reduced-motion). Safe with no DOM: resolves null immediately.
 */
export function showRoundPicker(opts: RoundPickerOpts): Promise<string | null> {
  if (typeof document === 'undefined') return Promise.resolve(null);

  const root = document.createElement('div');
  root.id = OVERLAY_ID;
  root.className = 'rpk-ov';
  root.setAttribute('role', 'dialog');
  root.setAttribute('aria-modal', 'true');
  root.setAttribute('aria-label', HEADLINE);

  const tiles = opts.groups
    .map((g, i) => {
      const count = g.count === 1 ? '1 question' : `${g.count} questions`;
      return (
        `<button type="button" class="rpk-tile ${tintClassForIndex(i)}" data-key="${escHtml(g.key)}">` +
        `<span class="rpk-tile-ic" aria-hidden="true">${icon(iconForGroup(g.key), 22)}</span>` +
        `<span class="rpk-tile-name">${escHtml(g.title)}</span>` +
        `<span class="rpk-tile-count">${count}</span>` +
        `</button>`
      );
    })
    .join('');

  root.innerHTML =
    `<div class="rpk-card">` +
    `<div class="rpk-top">` +
    `<div class="rpk-heading">` +
    `<div class="rpk-kicker">Round ${opts.roundNumber} of ${opts.totalRounds} · ${escHtml(opts.roundName)}</div>` +
    (opts.careerChip ? `<div class="rpk-chip">${escHtml(opts.careerChip)}</div>` : '') +
    `<h2 class="rpk-head">${HEADLINE}</h2>` +
    `</div>` +
    `<div class="rpk-theo" id="rpTheo" aria-hidden="true"></div>` +
    `</div>` +
    `<div class="rpk-grid">${tiles}</div>` +
    `<button type="button" class="btn rpk-mixed" id="rpMixed">Mixed. A little of everything.</button>` +
    `</div>`;

  const prev = document.getElementById(OVERLAY_ID);
  if (prev && prev.parentNode) prev.parentNode.removeChild(prev);
  host().appendChild(root);
  fillTheoBadge();
  probeHeadshot();

  return new Promise<string | null>((resolve) => {
    let settled = false;

    function onKey(e: KeyboardEvent): void {
      // Escape = the effortless default (Mixed). Enter/Space activate the
      // focused tile (the Mixed action holds initial focus) — handled HERE, in
      // the capture phase, and cancelled so nothing behind the picker (main's
      // global Enter handler — the home Start button, the next-round Start) can
      // see the key and launch a second run underneath us.
      if (e.key === 'Escape') {
        e.preventDefault();
        finish(null);
        return;
      }
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();
        const focused = document.activeElement as HTMLElement | null;
        if (focused && focused !== document.body && root.contains(focused)) focused.click();
        else finish(null); // focus wandered off the picker — one Enter is still Mixed
      }
    }

    function finish(key: string | null): void {
      if (settled) return;
      settled = true;
      document.removeEventListener('keydown', onKey, true);
      root.classList.add('mo-out');
      const remove = (): void => {
        if (root.parentNode) root.parentNode.removeChild(root);
      };
      if (prefersReducedMotion()) remove();
      else setTimeout(remove, 220);
      resolve(key);
    }

    root.querySelectorAll<HTMLButtonElement>('.rpk-tile').forEach((b) => {
      b.addEventListener('click', () => finish(b.dataset.key || null));
    });
    const mixed = root.querySelector<HTMLButtonElement>('#rpMixed');
    if (mixed) {
      mixed.addEventListener('click', () => finish(null));
      try {
        mixed.focus(); // one Enter (or one tap) = Mixed — the visually-primary default
      } catch {
        /* focus can throw in odd embeds — the picker still works by tap */
      }
    }
    document.addEventListener('keydown', onKey, true);
  });
}
